import  { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import { Label } from '@shadcn/components/ui/label';

import DraggableLocationList from './DraggableLocationList';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';
import { REGION_CODES, REGION_LABELS } from '../../constants/regions';

const CreateConfigModal = ({ locationsByRegion, onSuccess, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'FE',
    region: 'ALL',
    locationOrder: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);

  useEffect(() => {
    if (locationsByRegion) {
      if (formData.region && formData.region !== 'ALL') {
        
        const regionLocations = locationsByRegion[formData.region] || [];
        setAvailableLocations(regionLocations.map(loc => loc.name));
      } else {
        
        const allLocations = Object.values(locationsByRegion)
          .flat()
          .map(loc => loc.name);
        const uniqueLocations = [...new Set(allLocations)];
        setAvailableLocations(uniqueLocations);
      }
      setFormData(prev => ({ ...prev, locationOrder: [] }));
    }
  }, [formData.region, locationsByRegion]);

  const handleRegionChange = (region) => {
    setFormData(prev => ({ ...prev, region }));
  };

  const handleLocationOrderChange = (newOrder) => {
    setFormData(prev => ({ ...prev, locationOrder: newOrder }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.locationOrder.length === 0) {
      alert('Please arrange at least one location');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        type: formData.type,
        locationOrder: formData.locationOrder,
      };
      
      
      if (formData.region && formData.region !== 'ALL') {
        payload.region = formData.region;
      }
      
      await superagent
        .post(`${backendUrl}/locationOrderConfig/create-by-region`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .send(payload);
      
      onSuccess();
      handleReset();
    } catch (error) {
      console.error('Error creating config:', error);
      alert('An error occurred while creating the configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      type: 'FE',
      region: 'ALL',
      locationOrder: [],
    });
    setAvailableLocations([]);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create New Location Order Configuration</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select 
              value={formData.type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FE">FE (Frontend)</SelectItem>
                <SelectItem value="CSV">CSV (Export)</SelectItem>
                <SelectItem value="REPORT">Report</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="region">Region (Optional)</Label>
            <Select value={formData.region} onValueChange={handleRegionChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select region (or leave empty for all)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Regions</SelectItem>
                {REGION_CODES.map(region => (
                  <SelectItem key={region} value={region}>
                    {REGION_LABELS[region]} ({region})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {availableLocations.length > 0 && (
          <div className="space-y-3">
            <Label>Arrange Location Order</Label>
            <div className="text-sm text-gray-600 mb-2">
              {formData.region && formData.region !== 'ALL'
                ? `Drag and drop to arrange the order of locations for region ${formData.region}`
                : 'Drag and drop to arrange the order of locations for all regions'
              }
            </div>
            <DraggableLocationList
              availableLocations={availableLocations}
              selectedLocations={formData.locationOrder}
              onLocationOrderChange={handleLocationOrderChange}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting || formData.locationOrder.length === 0}
          >
            {isSubmitting ? 'Creating...' : 'Create Configuration'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default CreateConfigModal; 
